/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/anchor-is-valid */

import {useEffect} from 'react'

import {KTSVG} from '../../../../_metronic/helpers'
import {useGisContext} from '../GisContext'
import {useDrawV2} from '../Map/Draw/DrawV2Context'
import {useIsMobile} from '../../../shared/responsive'
import {
  AmbientMapLegend,
  ComparisonMapLegends,
  CommonMapLegend,
  shouldShowAmbientLegend,
  useMapLegendContext,
} from '../Fields/ViewField/ViewLand/Helpers/MapLegend'
import {useViewLandContext} from '../Fields/ViewField/ViewLand/ViewLandContext'
import {useViewFieldContext} from '../Fields/ViewField/ViewFieldContext'
import {useViewImageContext} from '../Fields/ViewField/ViewLand/Images/ViewImageContext'
import {useMapLayersV2} from '../Map/MapLayersV2Context'
import {mapState} from '../Map/MapState'
import {parseImageQuality} from '../Fields/ViewField/ViewLand/Images/Services/imageLayerPayload'

const clearDrawerOverlays = () => {
  document.querySelectorAll('.drawer-overlay').forEach((overlay) => {
    overlay.parentNode?.removeChild(overlay)
  })
}

const RightDrawer = ({children}) => {
  const isMobile = useIsMobile()
  const draw = useDrawV2()
  const {drawerTitle, isPanelExpanded, togglePanel} = useGisContext()
  const mapLegendContext = useMapLegendContext()
  const viewLandContext = useViewLandContext()
  const viewFieldContext = useViewFieldContext()
  const viewImageContext = useViewImageContext()
  const mapLayers = useMapLayersV2()
  const handleTitle = isMobile && draw.isDrawing ? `Dibujo - ${drawerTitle}` : drawerTitle
  const activeLandImage = viewImageContext.getActiveLandImage(viewLandContext.land?.land_id)
  const isComparisonSection = viewLandContext.activeSection === 'comparison'
  const hasComparisonLegends =
    !draw.isDrawing &&
    isComparisonSection &&
    mapLegendContext.comparisonLegends?.land_id === viewLandContext.land?.land_id &&
    (mapLegendContext.comparisonLegends?.items || []).length > 0
  const hideLegendForVisibleImage =
    viewLandContext.activeSection === 'images' &&
    activeLandImage?.imageType === 'rgb' &&
    activeLandImage?.selectedDate != null
  const hasBasicLegend =
    !isComparisonSection &&
    (!hideLegendForVisibleImage || parseImageQuality(mapLegendContext.basicLegend) != null) &&
    !draw.isDrawing &&
    mapLegendContext.ambient == null &&
    mapLegendContext.basicLegend != null &&
    viewLandContext.land != null &&
    mapLegendContext.activeLand === viewLandContext.land.land_id
  const hasAmbientLegend = shouldShowAmbientLegend({
    activeSection: viewLandContext.activeSection,
    ambient: mapLegendContext.ambient,
    blocked: isComparisonSection || hideLegendForVisibleImage || draw.isDrawing,
  })
  const legendKind = hasComparisonLegends
    ? 'comparison'
    : hasAmbientLegend
      ? 'ambient'
      : hasBasicLegend
        ? 'basic'
        : 'none'
  const hasVisibleLegend = legendKind !== 'none'
  const shouldRenderFloatingLegend = isMobile && hasVisibleLegend

  useEffect(() => {
    if (!isMobile) {
      return undefined
    }

    clearDrawerOverlays()

    return () => {
      clearDrawerOverlays()
    }
  }, [isMobile, isPanelExpanded])

  useEffect(() => {
    if (!isMobile || !mapState.map) {
      return undefined
    }

    const targetId = viewLandContext.land?.land_id || viewFieldContext.field?.field_id
    const syncMapViewport = () => {
      if (!mapState.map) {
        return
      }

      mapState.map.resize()

      if (targetId) {
        mapLayers.fit(targetId)
      }
    }

    const observerTarget = document.getElementById('kt_right_container')
    const legendTarget = document.getElementById('kt_right_mobile_legend')
    let animationFrameId = null
    let timeoutId = null
    let timeoutIdLate = null
    let resizeObserver = null

    const scheduleSync = () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId)
      }

      if (timeoutId != null) {
        clearTimeout(timeoutId)
      }

      if (timeoutIdLate != null) {
        clearTimeout(timeoutIdLate)
      }

      animationFrameId = requestAnimationFrame(syncMapViewport)
      timeoutId = setTimeout(syncMapViewport, 260)
      timeoutIdLate = setTimeout(syncMapViewport, 620)
    }

    scheduleSync()

    if ((observerTarget || legendTarget) && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        scheduleSync()
      })

      if (observerTarget) {
        resizeObserver.observe(observerTarget)
      }

      if (legendTarget) {
        resizeObserver.observe(legendTarget)
      }
    }

    return () => {
      if (animationFrameId != null) {
        cancelAnimationFrame(animationFrameId)
      }

      if (timeoutId != null) {
        clearTimeout(timeoutId)
      }

      if (timeoutIdLate != null) {
        clearTimeout(timeoutIdLate)
      }

      resizeObserver?.disconnect()
    }
  }, [
    draw.isDrawing,
    hasVisibleLegend,
    hideLegendForVisibleImage,
    isMobile,
    isPanelExpanded,
    legendKind,
    mapLayers,
    viewFieldContext.field?.field_id,
    viewLandContext.land?.land_id,
  ])

  return (
    <div
      id='kt_right_container'
      className={`bg-body ${
        isMobile
          ? `gis-mobile-drawer drawer drawer-end${isPanelExpanded ? ' drawer-on' : ''}`
          : ''
      }`.trim()}
      data-gis-mobile-panel-state={isPanelExpanded ? 'expanded' : 'collapsed'}
      data-gis-mobile-draw-active={draw.isDrawing ? 'true' : 'false'}
      data-gis-mobile-legend-active={hasVisibleLegend ? 'true' : 'false'}
      data-gis-mobile-legend-kind={legendKind}
    >
      {shouldRenderFloatingLegend && (
        <div id='kt_right_mobile_legend' className='gis-mobile-drawer__legend-floating'>
          {hasComparisonLegends && (
            <ComparisonMapLegends items={mapLegendContext.comparisonLegends.items} />
          )}
          {hasBasicLegend && <CommonMapLegend headers={mapLegendContext.basicLegend} />}
          {hasAmbientLegend && <AmbientMapLegend ambient={mapLegendContext.ambient} />}
        </div>
      )}
      <div className='card shadow-none w-100 gis-mobile-drawer__card'>
        <button
          type='button'
          className='gis-mobile-drawer__handle'
          onClick={togglePanel}
          aria-controls='kt_right_container'
          aria-expanded={isPanelExpanded}
        >
          <span className='gis-mobile-drawer__handle-copy'>{handleTitle}</span>
          <KTSVG
            path='/media/icons/duotune/arrows/arr072.svg'
            className='svg-icon-2 gis-mobile-drawer__handle-chevron'
          />
        </button>
        <div className='card-body p-0 gis-mobile-drawer__body' id='kt_explore_body'>
          <div className='gis-mobile-drawer__scroll'>{children}</div>
        </div>
      </div>
    </div>
  )
}

export default RightDrawer
